import { FC, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMutation } from 'react-query';
import { CellProps, Column } from 'react-table';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useDebounce } from 'hooks/useDebounce';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { AvatarIcon } from 'components/Core/AvatarIcon';
import { Button } from 'components/Core/Button';
import { ReactComponent as ReviewStar } from 'assets/review-star.svg';
import { ReactComponent as RemoveReview } from 'assets/remove-review.svg';
import { ReviewResponse } from 'stores/reviews/types';
import { reviewsTableTranslations } from './translations';
import { ReviewsTableTransFields } from './types';
import { NUMBER_OF_STAR } from './constants';
import styles from './reviewsTable.module.scss';

const ReviewsTable: FC = () => {
  const {
    reviewsStore: { manageReview, reviews, removeReview },
    popUpStore: { openPopUp, closePopUp },
  } = useStore();

  const { t } = useMyTranslations<ReviewsTableTransFields>([reviewsTableTranslations.name]);
  const [search, setSearch] = useState('');
  const [filtred, setFiltred] = useState(reviews || []);

  const searchD = useDebounce(search);

  const { mutate, isLoading } = useMutation(manageReview, {
    mutationKey: 'approveReview',
    onMutate: () => {
      closePopUp();
    },
  });

  const { mutate: remove, isLoading: isLoadingRemove } = useMutation(removeReview, {
    mutationKey: 'removeReview',
    onMutate: () => {
      closePopUp();
    },
  });

  useEffect(() => {
    setFiltred(reviews?.filter((item) => item.name.toLowerCase().includes(searchD.toLowerCase())) || []);
  }, [searchD, reviews]);

  const columns: Column<ReviewResponse>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original: { name, image },
          },
        }: CellProps<ReviewResponse>) => {
          return (
            <div className={styles.avatar}>
              <AvatarIcon avatar={image} name='image' size={56} styleProps={{ borderRadius: 16 }} />
              <p className={styles.name}>{name}</p>
            </div>
          );
        },
        accessor: (row) => row.name,
        disableSortBy: true,
      },
      {
        Header: t('address'),
        id: 'address',
        Cell: ({ row: { original } }: CellProps<ReviewResponse>) => {
          return <p>{original.address}</p>;
        },
        accessor: (row) => row.name,
        disableSortBy: true,
      },
      {
        Header: t('date'),
        id: 'date',
        Cell: ({ row: { original } }: CellProps<ReviewResponse>) => {
          const date = new Date(original.createdAt);

          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();

          const formattedDate = `${day}.${month}.${year}`;
          return <p className={styles.name}>{formattedDate}</p>;
        },
        accessor: (row) => row.createdAt,
        width: 100,
      },
      {
        Header: t('username'),
        id: 'username',
        Cell: ({ row: { original } }: CellProps<ReviewResponse>) => <p className={styles.name}>{original.userName}</p>,
        accessor: (row) => row.userName,
        width: 100,
      },
      {
        Header: t('reviewText'),
        id: 'text',
        Cell: ({
          row: {
            original: { text },
          },
        }: CellProps<ReviewResponse>) => {
          return <p className={styles.text}>{text}</p>;
        },
        accessor: (row) => row.text,
      },
      {
        Header: t('grade'),
        id: 'grade',
        Cell: ({ row: { original } }: CellProps<ReviewResponse>) => {
          return (
            <div className={styles.startsBox}>
              {Array.from({ length: NUMBER_OF_STAR }).map((_, index) => {
                const starColor = original.rating > index ? '#FFCF40' : '#E0E0E0';
                return <ReviewStar fill={starColor} stroke={starColor} key={index} />;
              })}
            </div>
          );
        },
        width: 110,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({
          row: {
            original: { moderation, id },
          },
        }: CellProps<ReviewResponse>) => {
          // const isDisabled = original.moderation !== 2;
          return (
            <div className={styles.actionsBox}>
              <Button
                title={t('approve')}
                isDisabled={moderation !== 'Pending' || isLoading || isLoadingRemove}
                onClick={() =>
                  openPopUp({
                    title: t('confirmation'),
                    description: t('approve-text'),
                    buttonLabel: t('confirm'),
                    formName: 'confirmation',
                    cb: () => mutate({ id, moderation: 'Approve' }),
                  })
                }
                stylesProp={{ padding: '8px 36px' }}
              />
              <Button
                title={t('decline')}
                isDisabled={moderation !== 'Pending' || isLoading || isLoadingRemove}
                onClick={() =>
                  openPopUp({
                    title: t('confirmation'),
                    description: t('decline-text'),
                    buttonLabel: t('confirm'),
                    formName: 'confirmation',
                    cb: () => mutate({ id, moderation: 'Declined' }),
                  })
                }
                stylesProp={{ padding: '8px 36px', background: 'linear-gradient(180deg, #EB5757 0%, #EC3E3E 100%)' }}
              />
              <button
                className={styles.reviewRemoveContainer}
                disabled={isLoading || isLoadingRemove}
                onClick={() => {
                  openPopUp({
                    title: t('confirmation'),
                    description: t('remove-text'),
                    buttonLabel: t('confirm'),
                    formName: 'confirmation',
                    cb: () => remove(id),
                  });
                }}>
                <RemoveReview className={styles.reviewRemove} />
              </button>
            </div>
          );
        },
        width: 210,
      },
    ],
    [reviews, isLoading, isLoadingRemove]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      <div className={styles.search}>
        <input
          type='text'
          className={styles.searchInput}
          placeholder={t('search')}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </div>
      {reviews && reviews.length > 0 ? (
        <Table columns={columns as never} data={filtred} simpleList isPaginated={reviews.length > PAGE_SIZE} />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
};

export default observer(ReviewsTable);
